import { Search } from 'lucide-react'
import { Button, Spinner } from '@/components/ds2'
import type { BoxTruckPinnedBoard } from '../utils/pinnedBoard'

/** Full-height board placeholders for the Box Truck loadboard: loading, empty, error, and the "New search"
 *  hero shown for a tab that hasn't run a search yet (the board never auto-runs an anywhere search). */

export const LoadingState = () => (
    <div className="flex flex-1 items-center justify-center py-24">
        <Spinner color="accent" size={24} />
        <span className="ml-2 text-sm text-foreground-muted">Loading loads…</span>
    </div>
)

/** What an empty board means, per board. A pinned board's wording has to name WHOSE action fills it:
 *  "No bids yet" on My Biddings reads as "nobody has bid", when what it says is "you have not bid". */
const EMPTY_COPY: Record<BoxTruckPinnedBoard | 'search', { title: string; hint: string }> = {
    driver_bids: { title: 'No bids yet', hint: 'Loads a driver bids on will appear here.' },
    my_bids: { title: 'You haven’t bid on anything yet', hint: 'Loads you send a bid email about will appear here.' },
    search: { title: 'No loads yet', hint: 'New Sylectus posts will appear here as they arrive.' },
}

export const EmptyState = ({ hasLoads, pinnedBoard }: { hasLoads: boolean; pinnedBoard: BoxTruckPinnedBoard | null }) => {
    const copy = EMPTY_COPY[pinnedBoard ?? 'search']
    return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="text-sm font-medium text-foreground">{hasLoads ? 'No loads match your filters' : copy.title}</p>
            <p className="mt-1 text-xs text-foreground-muted">{hasLoads ? 'Try clearing the search or widening the lane.' : copy.hint}</p>
        </div>
    )
}

export const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-medium text-danger">Couldn’t load the feed</p>
        <p className="mt-1 max-w-sm text-xs text-foreground-muted">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">
            Retry
        </Button>
    </div>
)

/** Shown when the active tab hasn't searched yet — mirrors Load Hub's "New search" empty state. No request has
 *  fired; the operator sets a lane + pickup and presses Search. */
export const NewSearchState = () => (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-24 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-default">
            <Search size={24} className="text-foreground-muted" />
        </div>
        <p className="mt-4 text-sm font-semibold text-foreground">Start a new search</p>
        <p className="mt-1 max-w-sm text-xs text-foreground-muted">
            Set your origin, destination, equipment and pickup window, then press Search to load Box Truck loads for that lane.
        </p>
    </div>
)
